import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons";

import FnrButton from "UI/FnrButton3";
import { FnrToggle, FnrToggleChild, FnrToggleItems } from "UI/FnrToggle2";
import { colors } from "UI/colors";

import { useIsSignedUpForItem } from "./utils/useIsSignedUpForItem";

const MAX_INITIAL_OPTIONS = 12;

export default function NumberOfItems({
  maxSlots,
  numberOfSlots,
  setNumberOfSlots,
  signupItem,
  handleCloseClick,
  numberOfPaidSlots,
}) {
  const inputRef = useRef();
  const [showInputUI, setShowInputUI] = useState(
    numberOfSlots > MAX_INITIAL_OPTIONS
  );
  const [resizeTrigger, setResizeTrigger] = useState(0);
  const isCurrentlySignedUp = useIsSignedUpForItem(signupItem.ID);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (isCurrentlySignedUp && numberOfPaidSlots > 0) {
      // An array from the current # of paid slots up to the maxSlots
      const newOptions = Array.from(
        { length: maxSlots - numberOfPaidSlots + 1 },
        (_, i) => numberOfPaidSlots + i
      );
      setOptions(newOptions);
    } else if (isCurrentlySignedUp) {
      // includes 0 option for cancelling signup
      const newOptions = Array.from({ length: maxSlots + 1 }, (v, k) => k);
      setOptions(newOptions);
    } else {
      // no 0 option
      const newOptions = Array.from({ length: maxSlots }, (v, k) => k + 1);
      setOptions(newOptions);
    }
  }, [maxSlots, isCurrentlySignedUp, numberOfPaidSlots]);

  const handleMoreButtonClick = () => {
    setShowInputUI(true);
    setNumberOfSlots(0);
    setResizeTrigger(resizeTrigger + 1);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 200);
  };

  const handleInputChange = (e) => {
    const { value } = e.target;

    const isValid = value <= maxSlots && (/^\d+$/.test(value) || value === ""); // numeric only (or an empty string, if you're hitting delete to start over)

    if (!isValid) return;
    setNumberOfSlots(value);
  };

  const optionsToDisplay = options.slice(0, MAX_INITIAL_OPTIONS);

  const renderItemsUI = () => {
    if (options.length === 1) {
      return <MessageWrapper>Sounds good! Ready to sign up?</MessageWrapper>;
    } else if (showInputUI) {
      return (
        <InputWrapper>
          <Input
            value={numberOfSlots || ""}
            onChange={handleInputChange}
            ref={inputRef}
          />
          <InputMax>The max number you may sign up for is {maxSlots}.</InputMax>
        </InputWrapper>
      );
    } else {
      return (
        <ToggleWrapper>
          <FnrToggle
            triggerResize={resizeTrigger}
            style={{
              border: "none",
              background: "transparent",
              color: "white",
            }}
          >
            <FnrToggleItems>
              {optionsToDisplay.map((option) => {
                return (
                  <FnrToggleChild
                    key={option}
                    selected={option === numberOfSlots}
                    style={{ flex: 1 }}
                  >
                    <ToggleButton onClick={() => setNumberOfSlots(option)}>
                      {option}
                    </ToggleButton>
                  </FnrToggleChild>
                );
              })}
            </FnrToggleItems>
          </FnrToggle>

          {optionsToDisplay.length < options.length ? (
            <MoreButtonWrapper>
              <FnrButton size="small" onClick={handleMoreButtonClick}>
                More Options
              </FnrButton>
            </MoreButtonWrapper>
          ) : null}
        </ToggleWrapper>
      );
    }
  };

  return (
    <>
      <HeaderBar>
        {options.length === 1
          ? "Only 1 item is available."
          : "How many items would you like to sign up for?"}

        <CloseButton onClick={handleCloseClick}>
          <FontAwesomeIcon icon={faTimesCircle} />
        </CloseButton>
      </HeaderBar>
      {renderItemsUI()}
    </>
  );
}

const HeaderBar = styled.div`
  position: relative;
  background-image: linear-gradient(159deg, #2f3f4f 5%, #13426b 100%);
  border-radius: 11px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  padding: 10px;
  padding-right: 40px;

  @media (max-width: 400px) {
    font-size: 14px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 4px;
  right: 10px;
  font-size: 24px;
  opacity: 0.9;
  cursor: pointer;
  transition: opacity 150ms;

  &:hover {
    opacity: 1;
  }

  &:active {
    transform: scale(0.97);
  }
`;

const MessageWrapper = styled.div`
  text-align: center;
  padding: 10px;
`;

const ToggleWrapper = styled.div`
  padding: 5px;
  max-height: 400px;
`;

const ToggleButton = styled.div`
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MoreButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px 10px;
`;

const Input = styled.input`
  margin: 0 auto;
  display: block;
  width: 120px;
  text-align: center;
  background: #efefef;
  border: 1px solid #efefef;
  border: none;
  font-size: 38px;
  padding: 5px 10px;
  border-radius: 8px;
  font-family: "Open Sans", sans-serif;
  color: #3b3b3b;
  outline: none;
  transition: box-shadow 150ms;

  &:focus {
    box-shadow: 0 0 0 2px ${colors.fnBlue};
  }
`;

const InputMax = styled.div`
  margin-top: 4px;
  color: #333;
  font-size: 13px;
  text-align: center;
  font-weight: 400;
`;
